(() => {
  const processedSelects = new WeakSet();

  const stripStatusText = (value) =>
    (value || '')
      .replace(/\s*[—-]\s*(?:sold out|unavailable)\s*$/i, '')
      .trim();

  const formatMoney = (cents, currency) => {
    const numeric = Number(cents);
    if (!Number.isFinite(numeric)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: numeric % 100 === 0 ? 0 : 2,
      }).format(numeric / 100);
    } catch (_) {
      return `${(numeric / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const variantLabel = (variant, fallback = '') => {
    const values = Array.isArray(variant?.options)
      ? variant.options.filter((value) => value && value !== 'Default Title')
      : [];

    if (values.length) return values.join(' · ');

    const title = stripStatusText(variant?.title || fallback);
    return title && title !== 'Default Title' ? title : fallback;
  };

  const initSelect = async (select) => {
    if (!select || processedSelects.has(select)) return;

    const picker = select.closest('.cp-sticky-atc__picker');
    const sticky = select.closest('.cp-sticky-atc');
    const root = sticky?.closest("[id^='MainProduct-']");
    if (!picker || !sticky || !root) return;

    processedSelects.add(select);
    select.classList.add('cp-sticky-atc__select--customized');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    const currency = window.Shopify?.currency?.active || 'USD';
    let productData = null;

    try {
      const response = await fetch(`${root.dataset.url}.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) productData = await response.json();
    } catch (_) {
      productData = null;
    }

    const fallbackVariants = Array.from(select.options).map((option) => ({
      id: option.value,
      title: stripStatusText(option.textContent),
      options: [stripStatusText(option.textContent)],
      available: !option.disabled,
      price: null,
    }));

    const variants = productData?.variants?.length ? productData.variants : fallbackVariants;
    const variantsById = new Map(variants.map((variant) => [String(variant.id), variant]));
    const titleElement = picker.querySelector('.cp-sticky-atc__variant-title');
    const metaElement = picker.querySelector('.cp-sticky-atc__variant-meta');

    const menuId = `CpStickyVariantMenu-${root.dataset.section || Math.random().toString(36).slice(2)}`;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cp-sticky-atc__picker-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', menuId);

    const menu = document.createElement('div');
    menu.id = menuId;
    menu.className = 'cp-sticky-atc__menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', 'Product variants');
    menu.hidden = true;

    const optionElements = [];

    variants.forEach((variant) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'cp-sticky-atc__option';
      option.dataset.variantId = String(variant.id);
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('aria-disabled', variant.available === false ? 'true' : 'false');
      option.tabIndex = -1;

      const optionTitle = document.createElement('span');
      optionTitle.className = 'cp-sticky-atc__option-title';
      optionTitle.textContent = variantLabel(variant, variant.title || 'Option');

      const optionMeta = document.createElement('span');
      optionMeta.className = 'cp-sticky-atc__option-meta';
      const priceText = formatMoney(variant.price, currency);
      optionMeta.textContent = [priceText, variant.available === false ? 'Sold out' : ''].filter(Boolean).join(' · ');
      if (!optionMeta.textContent) optionMeta.hidden = true;

      option.append(optionTitle, optionMeta);
      menu.appendChild(option);
      optionElements.push(option);
    });

    picker.append(trigger, menu);

    const enabledOptions = () => optionElements.filter((option) => option.getAttribute('aria-disabled') !== 'true');

    const syncSelected = (variantId) => {
      const id = String(variantId || select.value || '');
      const variant = variantsById.get(id);
      if (!variant) return;

      const label = variantLabel(variant, variant.title || 'Choose option');
      const priceText = formatMoney(variant.price, currency);

      if (titleElement) titleElement.textContent = label;
      if (metaElement && priceText) metaElement.textContent = priceText;

      trigger.setAttribute('aria-label', `Choose product variant. Current selection: ${label}`);

      optionElements.forEach((option) => {
        option.setAttribute('aria-selected', option.dataset.variantId === id ? 'true' : 'false');
      });
    };

    const closeMenu = ({ restoreFocus = false } = {}) => {
      if (menu.hidden) return;
      menu.hidden = true;
      picker.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus();
    };

    const openMenu = (focusPosition = null) => {
      if (!menu.hidden) return;
      menu.hidden = false;
      picker.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      syncSelected(select.value);

      if (focusPosition) {
        const options = enabledOptions();
        if (!options.length) return;
        const target = focusPosition === 'last' ? options[options.length - 1] : options[0];
        requestAnimationFrame(() => target.focus());
      }
    };

    const selectVariant = (variantId) => {
      const id = String(variantId);
      const variant = variantsById.get(id);
      if (!variant || variant.available === false) return;

      select.value = id;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      syncSelected(id);
      closeMenu({ restoreFocus: true });
    };

    trigger.addEventListener('click', () => {
      if (menu.hidden) openMenu();
      else closeMenu();
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (menu.hidden) openMenu(event.key === 'ArrowUp' ? 'last' : 'first');
        else {
          const options = enabledOptions();
          if (!options.length) return;
          (event.key === 'ArrowUp' ? options[options.length - 1] : options[0]).focus();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
      }
    });

    optionElements.forEach((option) => {
      option.addEventListener('click', () => {
        if (option.getAttribute('aria-disabled') === 'true') return;
        selectVariant(option.dataset.variantId);
      });

      option.addEventListener('keydown', (event) => {
        const options = enabledOptions();
        const currentIndex = options.indexOf(option);
        if (currentIndex < 0) return;

        let nextIndex = null;
        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
        if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = options.length - 1;

        if (nextIndex !== null) {
          event.preventDefault();
          options[nextIndex].focus();
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu({ restoreFocus: true });
        }
      });
    });

    select.addEventListener('change', () => {
      requestAnimationFrame(() => syncSelected(select.value));
    });

    root.addEventListener('change', (event) => {
      if (event.target === select) return;
      window.setTimeout(() => syncSelected(select.value), 80);
      window.setTimeout(() => syncSelected(select.value), 700);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data || String(data.sectionId) !== String(root.dataset.section) || !data.variant) return;
        window.setTimeout(() => syncSelected(data.variant.id), 0);
      });
    }

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!menu.hidden && !picker.contains(event.target)) closeMenu();
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) closeMenu({ restoreFocus: true });
    });

    const visibilityObserver = new MutationObserver(() => {
      if (!sticky.classList.contains('is-visible')) closeMenu();
    });
    visibilityObserver.observe(sticky, { attributes: true, attributeFilter: ['class'] });

    syncSelected(select.value);
  };

  const scan = (scope = document) => {
    scope.querySelectorAll?.('.cp-sticky-atc__select').forEach((select) => initSelect(select));
    if (scope.matches?.('.cp-sticky-atc__select')) initSelect(scope);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scan();
        observer.observe(document.body, { childList: true, subtree: true });
      },
      { once: true }
    );
  } else {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
